import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import { useMemo, useEffect } from 'react';
import { useSceneNode } from '..';
export function useUniformSampler2d(gl, program, name, texture) {
  var scene = useSceneNode();
  var unit = useMemo(function () {
    return scene.getTextureUnit(texture);
  }, [scene, texture]);
  var memoized = useMemo(function () {
    var location = gl.getUniformLocation(program, name);
    gl.uniform1i(location, unit);
    return location;
  }, [gl, program, name, unit]);
  useEffect(function () {
    return function () {
      return scene.releaseTextureUnit(unit);
    };
  }, [scene, unit]);
  return [unit, memoized];
}
export function useMatrixUniform(gl, program, name, value, type) {
  var memoized = useMemo(function () {
    return gl.getUniformLocation(program, name);
  }, [gl, program, name]);
  useEffect(function () {
    gl.useProgram(program);
    gl[type](memoized, false, value);
  }, [gl, program, type, memoized, value]);
  return memoized;
}
export function useUniformMatrix2fv(gl, program, name, value) {
  return useMatrixUniform(gl, program, name, value, 'uniformMatrix2fv');
}
export function useUniformMatrix3fv(gl, program, name, value) {
  return useMatrixUniform(gl, program, name, value, 'uniformMatrix3fv');
}
export function useUniformMatrix4fv(gl, program, name, value) {
  return useMatrixUniform(gl, program, name, value, 'uniformMatrix4fv');
}
export function useVectorUniform(gl, program, name, value, type) {
  var memoized = useMemo(function () {
    return gl.getUniformLocation(program, name);
  }, [gl, program, name]);
  useEffect(function () {
    gl.useProgram(program);
    gl[type](memoized, value);
  }, [gl, program, type, memoized, value]);
  return memoized;
}
export function useUniform1f(gl, program, name, value) {
  return useVectorUniform(gl, program, name, value, 'uniform1f');
}
export function useUniform1i(gl, program, name, value) {
  return useVectorUniform(gl, program, name, value, 'uniform1i');
}
export function useUniform1fv(gl, program, name, value) {
  return useVectorUniform(gl, program, name, value, 'uniform1fv');
}
export function useUniform1iv(gl, program, name, value) {
  return useVectorUniform(gl, program, name, value, 'uniform1iv');
}
export function useUniform2fv(gl, program, name, value) {
  return useVectorUniform(gl, program, name, value, 'uniform2fv');
}
export function useUniform2iv(gl, program, name, value) {
  return useVectorUniform(gl, program, name, value, 'uniform2iv');
}
export function useUniform3fv(gl, program, name, value) {
  return useVectorUniform(gl, program, name, value, 'uniform3fv');
}
export function useUniform3iv(gl, program, name, value) {
  return useVectorUniform(gl, program, name, value, 'uniform3iv');
}
export function useUniform4fv(gl, program, name, value) {
  return useVectorUniform(gl, program, name, value, 'uniform4fv');
}
export function useUniform4iv(gl, program, name, value) {
  return useVectorUniform(gl, program, name, value, 'uniform4iv');
}
export function useVectorValueUniform(gl, program, name, type, values) {
  var memoized = useMemo(function () {
    return gl.getUniformLocation(program, name);
  }, [gl, program, name]);
  useEffect(function () {
    gl.useProgram(program);
    gl[type].apply(gl, [memoized].concat(_toConsumableArray(values)));
  }, [gl, program, type, memoized].concat(_toConsumableArray(values))); // eslint-disable-line

  return memoized;
}
export function useUniform2f(gl, program, name) {
  for (var _len = arguments.length, values = new Array(_len > 3 ? _len - 3 : 0), _key = 3; _key < _len; _key++) {
    values[_key - 3] = arguments[_key];
  }

  return useVectorValueUniform(gl, program, name, 'uniform2f', values);
}
export function useUniform2i(gl, program, name) {
  for (var _len2 = arguments.length, values = new Array(_len2 > 3 ? _len2 - 3 : 0), _key2 = 3; _key2 < _len2; _key2++) {
    values[_key2 - 3] = arguments[_key2];
  }

  return useVectorValueUniform(gl, program, name, 'uniform2i', values);
}
export function useUniform3f(gl, program, name) {
  for (var _len3 = arguments.length, values = new Array(_len3 > 3 ? _len3 - 3 : 0), _key3 = 3; _key3 < _len3; _key3++) {
    values[_key3 - 3] = arguments[_key3];
  }

  return useVectorValueUniform(gl, program, name, 'uniform3f', values);
}
export function useUniform3i(gl, program, name) {
  for (var _len4 = arguments.length, values = new Array(_len4 > 3 ? _len4 - 3 : 0), _key4 = 3; _key4 < _len4; _key4++) {
    values[_key4 - 3] = arguments[_key4];
  }

  return useVectorValueUniform(gl, program, name, 'uniform3i', values);
}
export function useUniform4f(gl, program, name) {
  for (var _len5 = arguments.length, values = new Array(_len5 > 3 ? _len5 - 3 : 0), _key5 = 3; _key5 < _len5; _key5++) {
    values[_key5 - 3] = arguments[_key5];
  }

  return useVectorValueUniform(gl, program, name, 'uniform4f', values);
}
export function useUniform4i(gl, program, name) {
  for (var _len6 = arguments.length, values = new Array(_len6 > 3 ? _len6 - 3 : 0), _key6 = 3; _key6 < _len6; _key6++) {
    values[_key6 - 3] = arguments[_key6];
  }

  return useVectorValueUniform(gl, program, name, 'uniform4i', values);
}